
import JSZip from "jszip";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { DataGraphicsResultAndProcess, DerechoDto, HeatMapData, MapBaseData, OpenDataIndicator } from "../../../../interfaces/OpenData";
import { INDICATOR_TYPE_STRUCTURAL } from "../../../../utils/const";
import { buildPivotByAnioEncabezado, buildStructuralColumns, escapeCSV, saveAsExcel, toExcelBuffer, toUTF8CSV } from "./helpers";
import { DownloadFormat } from "./types";

export interface DownloadCSVOptions {
    indicators: OpenDataIndicator[];
    format?: DownloadFormat;
}

//Base de descarga: individual
export async function downloadIndicatorCSV({ indicators, format = 'csv' }: DownloadCSVOptions) {
    if (!indicators.length) return;

    const isExcel = format === 'excel';
    const ext = isExcel ? 'xlsx' : 'csv';
    const zip = new JSZip();
    const singleIndicator = indicators.length === 1;

    const addToZip = async (
        zipTarget: JSZip | null,
        name: string,
        rows: unknown[][]
    ) => {
        if (!zipTarget) return;
        if (isExcel) {
            zipTarget.file(`${name}.${ext}`, toExcelBuffer(rows));
        } else {
            zipTarget.file(`${name}.csv`, toUTF8CSV(rows));
        }
    };

    for (const indicator of indicators) {
        const { tipo_indicador, data, evaluaciones, maps, titulo: indicatorTitle = "indicador" } = indicator;
        const isEstructural = tipo_indicador === INDICATOR_TYPE_STRUCTURAL;

        const folder = !isEstructural && !singleIndicator ? zip.folder(indicatorTitle) : zip;

        if (isEstructural) {
            if (data && Array.isArray(data) && data.length > 0) {
                const derechos = data as DerechoDto[];
                const columnas = buildStructuralColumns(derechos)

                const rows = [
                    ["", "", ...columnas.map(c => `Derecho: ${c.derecho}`)],
                    ["", "", ...columnas.map(c => `Dimension: ${c.dimension}`)],
                    ["Ley", "Año", ...columnas.map(c => `Subdimension: ${c.subdimension}`)],
                    [
                        escapeCSV(indicator.titulo),
                        new Date(indicator.sancion ?? "").getFullYear().toString(),
                        ...columnas.map(c => escapeCSV(c.evaluacion)),
                    ],
                ];

                if (singleIndicator) {
                    if (isExcel) {
                        await saveAsExcel(rows, indicatorTitle.replace(/\s+/g, "_"));
                    } else {
                        saveAs(
                            new Blob([toUTF8CSV(rows)], { type: "text/csv;charset=utf-8" }),
                            `${indicatorTitle.replace(/\s+/g, "_")}.csv`
                        );
                    }
                    return;
                }

                await addToZip(zip, indicatorTitle, rows);
                continue;
            }
        } else {
            // --- EVALUACIONES ---
            if (evaluaciones && Object.keys(evaluaciones).length > 0) {
                const hasValues = Object.values(evaluaciones).some(valor => valor !== null);

                if (hasValues) {
                    const rows = [
                        ["Año", "Evaluación"],
                        ...Object.entries(evaluaciones).map(([anio, valor]) => [anio, valor]),
                    ];
                    await addToZip(folder, `${indicatorTitle} - Evaluaciones`, rows);
                }
            }

            // --- GRÁFICOS ---
            if (data && Array.isArray(data) && data.length > 0) {
                const datasets = Array.isArray(data[0])
                    ? (data as unknown as DataGraphicsResultAndProcess[][])
                    : [data as DataGraphicsResultAndProcess[]];

                for (const [index, dataset] of datasets.entries()) {
                    const rows = buildPivotByAnioEncabezado(dataset);

                    await addToZip(
                        folder,
                        `${indicatorTitle} - Gráfico${datasets.length > 1 ? `${index + 1}` : ""}`,
                        rows
                    );
                }
            }
            // --- MAPAS ---
            if (maps && maps.length > 0) {
                maps.forEach((map, index) => {
                    if (map.type === "base") {
                        const baseData = (map.data as MapBaseData[]).map((item) => {
                            const { coordenadas, anio, ...rest } = item;
                            return {
                                coordenadas: Array.isArray(coordenadas)
                                    ? `"${coordenadas.join(", ")}"`
                                    : coordenadas,
                                Año: anio,
                                ...rest,
                            };
                        });

                        if (isExcel) {
                            const headers = Object.keys(baseData[0] ?? {});
                            const rows = [headers, ...baseData.map(r => headers.map(h => (r as any)[h] ?? ""))];
                            folder?.file(
                                `${indicatorTitle} - Mapa ${index + 1} - Mapa base.xlsx`,
                                toExcelBuffer(rows)
                            );
                        } else {
                            folder?.file(
                                `${indicatorTitle} - Mapa ${index + 1} - Mapa base.csv`,
                                toUTF8CSV(baseData)
                            );
                        }
                    } else if (map.type === "heat") {
                        const heatData = map.data as HeatMapData[];
                        const isNewFormat = heatData.some((h) => !!h.encabezado);

                        if (isNewFormat) {
                            const rows = buildPivotByAnioEncabezado(heatData);
                            addToZip(folder, `${indicatorTitle} - Mapa ${index + 1} - Mapa de calor`, rows);
                            return;
                        }

                        const comunas = Array.from(new Set(heatData.map((h) => h.comuna))).sort((a, b) => Number(a) - Number(b));
                        const anios = Array.from(
                            new Set(heatData.map((h) => h.anio).filter((a): a is string => !!a))
                        ).sort((a, b) => Number(a) - Number(b));

                        const pivot = comunas.map((comuna) => {
                            const row: Record<string, string | number> = { Comuna: comuna };
                            anios.forEach((anio) => {
                                const val = heatData.find((h) => h.comuna === comuna && h.anio === anio)?.valor ?? "";
                                row[anio as string] = val;
                            });
                            return row;
                        });

                        const orderedPivot = pivot.map((row) => {
                            const ordered: Record<string, string | number> = { Comuna: row["comuna"] };
                            anios.forEach((anio) => { ordered[anio as string] = row[anio as string]; });
                            return ordered;
                        });

                        if (isExcel) {
                            const rows = [["Comuna", ...anios], ...comunas.map(comuna =>
                                [comuna, ...anios.map(anio =>
                                    heatData.find(h => h.comuna === comuna && h.anio === anio)?.valor ?? ""
                                )]
                            )];
                            folder?.file(
                                `${indicatorTitle} - Mapa ${index + 1} - Mapa de calor.xlsx`,
                                toExcelBuffer(rows)
                            );
                        } else {
                            const csv = Papa.unparse(orderedPivot, { columns: ["Comuna", ...anios] });
                            folder?.file(
                                `${indicatorTitle} - Mapa ${index + 1} - Mapa de calor.csv`,
                                "\uFEFF" + csv
                            );
                        }
                    }
                });
            }
        }
    }

    // --- DESCARGA ÚNICA ---
    const content = await zip.generateAsync({ type: "blob" });
    if (singleIndicator) {
        const files = Object.keys(zip.files);
        const isSingleFile = files.length === 1 && files[0].endsWith(`.${ext}`);

        if (isSingleFile) {
            const file = await zip.file(files[0])?.async(isExcel ? "arraybuffer" : "string");
            if (file) {
                saveAs(
                    new Blob([file], { type: isExcel ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv;charset=utf-8" }),
                    files[0]
                );
                return;
            }
        }
        saveAs(content, `${indicators[0].titulo}.zip`);
    } else {
        saveAs(content, `Datos_de_indicadores_-_MDDM.zip`);
    }
}